import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Points } from 'components/Points/Points';
import { FoilCardImage } from 'components/FoilCardImage';
import { AssetUrls } from 'config/assets';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { SidebarTradeCard } from 'screens/Public/UserProfile/context/ProfileTradeSidebarContext';
import { SUPPORTED_LANGUAGES, getLanguageDisplayName, normalizeLanguageCode, formatLanguage, BULK_TIER_POINTS_THRESHOLD } from 'constants/languages';
import { CardFinish, FINISH_PRICE_KEY, FinishSource, finishFields, finishFromRow, getAvailableFinishes, haveFinishFromRow, isEstimatedPriceKey, offerableFinishes, PRICE_KEY_LABELS, PriceKey, toHaveFinish } from 'utils/finishes';
import { FinishSelect } from 'components/FinishSelect';
import './StagedCardItem.css';
import { NoPrice } from 'components/NoPrice/NoPrice';
import { dropsPriceOnRepriceError, hasRealPrice } from 'utils/price';

interface StagedCardItemProps {
  card: SidebarTradeCard;
  onUpdate: (id: string, updates: Partial<SidebarTradeCard>) => void;
  onRemove: (id: string) => void;
  onCalculatingChange?: (id: string, isCalculating: boolean) => void;
}

const CONDITION_OPTIONS = [
  { value: 'NEAR_MINT', label: 'NM' },
  { value: 'SLIGHTLY_PLAYED', label: 'LP' },
  { value: 'MODERATELY_PLAYED', label: 'MP' },
  { value: 'HEAVILY_PLAYED', label: 'HP' },
];

const LANGUAGE_OPTIONS = SUPPORTED_LANGUAGES.map(l => ({
  value: l.code.toLowerCase(),
  label: l.abbreviation,
}));

export const StagedCardItem: React.FC<StagedCardItemProps> = ({ card, onUpdate, onRemove, onCalculatingChange }) => {
  const [calculatePrice] = useCalculatePriceMutation();
  const [isCalculating, setIsCalculating] = useState(false);
  const hasInitialFetch = useRef(false);

  // Always recalculate price on mount to ensure displayed price matches card parameters
  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchNewPrice({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSmallImageUrl = (url: string): string => {
    return url?.replace('/large/', '/small/')?.replace('/normal/', '/small/') || url;
  };

  const fetchNewPrice = async (updates: Partial<SidebarTradeCard>) => {
    const updatedCard = { ...card, ...updates };
    try {
      setIsCalculating(true);
      onCalculatingChange?.(card.id, true);
      const result = await calculatePrice({
        cards: [{
          wantListId: updatedCard.wantListId,
          cardId: updatedCard.cardId?.toString(),
          condition: updatedCard.condition,
          language: updatedCard.language,
          ...finishFields(haveFinishFromRow(updatedCard)),
          set: updatedCard.set,
        }]
      }).unwrap();

      const cardResult = result.cards[0];
      if (cardResult) {
        onUpdate(card.id, {
          ...updates,
          // A null or non-positive slot is "no price", never a number to stage at
          pointValue: hasRealPrice(cardResult.displayPrice) ? cardResult.displayPrice : null,
          languageMultiplierApplied: cardResult.languageMultiplierApplied ?? false,
          multiplierValue: cardResult.multiplierValue ?? 1.0,
          // Forwarded only when the backend sent it (Track A): an absent field means
          // the deploy that emits it has not happened, not "no price resolved".
          ...(cardResult.priceKey !== undefined ? { priceKey: cardResult.priceKey } : {}),
        });
      } else {
        onUpdate(card.id, updates);
      }
    } catch (error) {
      console.error('Failed to calculate price:', error);
      // A lone no-price card is refused with a 400, and a failure after the row
      // changed leaves the old number describing a different card: no price.
      onUpdate(card.id, dropsPriceOnRepriceError(error, updates) ? { ...updates, pointValue: null } : updates);
    } finally {
      setIsCalculating(false);
      onCalculatingChange?.(card.id, false);
    }
  };

  const handleSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    // Find first matching printing for this set+collectorNumber combo
    const selectedSetDetail = card.acceptableSetDetails?.find(s => {
      const optionValue = `${s.code}-${s.collectorNumber || ''}`;
      return optionValue === selectedValue;
    });

    if (selectedSetDetail) {
      const newAvailableInFoil = selectedSetDetail.availableInFoil ?? card.availableInFoil;
      const newAvailableInNonFoil = selectedSetDetail.availableInNonFoil ?? card.availableInNonFoil;

      const updates: Partial<SidebarTradeCard> = {
        setCode: selectedSetDetail.code,
        set: selectedSetDetail.name,
        availableInFoil: newAvailableInFoil,
        availableInNonFoil: newAvailableInNonFoil,
      };

      if (selectedSetDetail.cardId) updates.cardId = selectedSetDetail.cardId;
      if (selectedSetDetail.imageUrl) updates.imageUrl = selectedSetDetail.imageUrl;
      // availableLanguages describes the original printing only; another set's
      // languages are unknown here, so fall back to the accepted list.
      if (card.availableLanguages && !card.availableLanguages.some(l => l.setCode === selectedSetDetail.code)) {
        updates.availableLanguages = undefined;
      }

      // Three-value auto-switch: keep the current finish when the new printing has it
      // and the want accepts it, else the first offerable one
      const newSource: FinishSource = selectedSetDetail.availableFinishes
        ? { finishes: selectedSetDetail.availableFinishes }
        : { availableInFoil: newAvailableInFoil, availableInNonFoil: newAvailableInNonFoil };
      updates.availableFinishes = getAvailableFinishes(newSource);
      const offerableNow = offerableFinishes(newSource, card);
      if (!offerableNow.includes(finishFromRow(card))) {
        Object.assign(updates, finishFields(toHaveFinish(offerableNow[0] ?? getAvailableFinishes(newSource)[0])));
      }

      fetchNewPrice(updates);
    }
  };

  const handleFinishChange = (finish: CardFinish) => {
    fetchNewPrice(finishFields(toHaveFinish(finish)));
  };

  const handleConditionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    fetchNewPrice({ condition: e.target.value });
  };

  // Language changes the language multiplier, so re-price like the other controls
  // instead of only updating the field (which left a stale price on screen).
  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value;
    const updates: Partial<SidebarTradeCard> = { language: newLang };
    // Switch to that language's printing: the card id is part of the priced tuple
    const variant = card.availableLanguages?.find(l => l.lang === newLang);
    if (variant?.id != null) {
      updates.cardId = variant.id;
      updates.scryfallId = variant.scryfallId;
      updates.imageUrl = AssetUrls.cards.getCardImage(variant.scryfallId, 'normal');
    }
    fetchNewPrice(updates);
  };

  // Finishes this staged copy can be sent in: what the printing has AND the want accepts
  const finishSource: FinishSource = card.availableFinishes
    ? { finishes: card.availableFinishes }
    : { availableInFoil: card.availableInFoil ?? false, availableInNonFoil: card.availableInNonFoil ?? true };
  const offerable = offerableFinishes(finishSource, card);
  const currentFinish = finishFromRow(card);

  // priceKey marker (backend Track A): a staged copy is priced for ONE finish, so its
  // own key is that finish's column; a price resolved through another column is an
  // estimate. (The want's accepted set is not the own-key set here: the copy being
  // sent has a definite finish.)
  const ownPriceKey: PriceKey = FINISH_PRICE_KEY[currentFinish];
  const estimatedFromKey = isEstimatedPriceKey(card.priceKey, [ownPriceKey]) ? card.priceKey : null;

  const acceptableConditions = card.acceptableConditions || CONDITION_OPTIONS.map(c => c.value);
  const availableConditions = CONDITION_OPTIONS.filter(c => acceptableConditions.includes(c.value));

  // Only offer the languages the wanter accepts on their want record. When
  // acceptableLanguages is empty/missing, fall back to all supported languages
  // (preserves prior behavior, avoids over-restricting). When the row says which
  // languages the printing exists in, offer only those: calculate-price and
  // initiateTrade refuse a language the printing does not exist in.
  const languageOptions = useMemo(() => {
    const accepted = card.acceptableLanguages?.length
      ? LANGUAGE_OPTIONS.filter(opt =>
          card.acceptableLanguages!.some(lang => normalizeLanguageCode(lang) === opt.value)
        )
      : LANGUAGE_OPTIONS;
    const existing = card.availableLanguages?.map(l => normalizeLanguageCode(l.lang));
    if (!existing?.length) return accepted;
    return accepted.filter(opt => existing.includes(opt.value));
  }, [card.acceptableLanguages, card.availableLanguages]);

  // Deduplicate acceptableSetDetails by setCode + collectorNumber so each
  // set appears once in the dropdown even when multiple language printings exist
  const dedupedSetDetails = useMemo(() => {
    if (!card.acceptableSetDetails) return [];
    const seen = new Map<string, typeof card.acceptableSetDetails[0]>();
    for (const sd of card.acceptableSetDetails) {
      const key = `${sd.code}-${sd.collectorNumber || ''}`;
      if (!seen.has(key)) {
        seen.set(key, sd);
      }
    }
    return Array.from(seen.values());
  }, [card]);

  const hasMultipleSets = dedupedSetDetails.length > 1;
  // Find the collector number for the current set from acceptableSetDetails
  const currentCollectorNumber = card.acceptableSetDetails?.find(s => s.code === card.setCode)?.collectorNumber || '';
  const currentSetValue = `${card.setCode}-${currentCollectorNumber}`;

  return (
    <div className="staged-card-item">
      <div className="staged-card-image">
        <FoilCardImage
          src={getSmallImageUrl(card.imageUrl)}
          alt={card.cardName}
          finish={finishFromRow(card)}
          borderRadius="4px"
          style={{ width: '50px', height: '70px', objectFit: 'cover' }}
        />
      </div>

      <div className="staged-card-details">
        <div className="staged-card-name" title={card.cardName}>
          {card.cardName}
        </div>

        <div className="staged-card-dropdowns">
          {hasMultipleSets ? (
            <select
              value={currentSetValue}
              onChange={handleSetChange}
              className="staged-card-select set-select"
              disabled={isCalculating}
            >
              {[...dedupedSetDetails]
                .sort((a, b) => {
                  const nameCompare = a.name.localeCompare(b.name);
                  if (nameCompare !== 0) return nameCompare;
                  return parseInt(a.collectorNumber || '0', 10) - parseInt(b.collectorNumber || '0', 10);
                })
                .map(setDetail => {
                  const uniqueValue = `${setDetail.code}-${setDetail.collectorNumber || ''}`;
                  const displayText = setDetail.collectorNumber
                    ? `${setDetail.name} #${setDetail.collectorNumber}`
                    : setDetail.name;
                  return <option key={uniqueValue} value={uniqueValue}>{displayText}</option>;
                })}
            </select>
          ) : (
            <span className="staged-card-set-text">{card.set}</span>
          )}

          <FinishSelect
            value={currentFinish}
            offerable={offerable}
            pointsByFinish={{ etched: card.etchedPointValue }}
            onChange={handleFinishChange}
            disabled={isCalculating}
            selectClassName="staged-card-select foil-select"
            staticClassName="staged-card-static-text"
          />

          {availableConditions.length > 1 ? (
            <select
              value={card.condition}
              onChange={handleConditionChange}
              className="staged-card-select condition-select"
              disabled={isCalculating}
            >
              {availableConditions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <span className="staged-card-static-text">
              {availableConditions[0]?.label || 'NM'}
            </span>
          )}

          {languageOptions.length > 1 ? (
            <select
              value={card.language}
              onChange={handleLanguageChange}
              className="staged-card-select language-select"
              disabled={isCalculating}
            >
              {languageOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          ) : (
            <span className="staged-card-static-text">
              {languageOptions[0]?.label || formatLanguage(card.language)}
            </span>
          )}
        </div>

        <div className="staged-card-price">
          {isCalculating ? (
            <span className="staged-card-price-loading">...</span>
          ) : hasRealPrice(card.pointValue) ? (
            <Points points={card.pointValue} type="small" />
          ) : (
            <NoPrice />
          )}
          {!isCalculating && estimatedFromKey && (
            <span
              className="staged-card-price-annotation"
              data-testid="price-key-marker"
              title={`No ${PRICE_KEY_LABELS[ownPriceKey]} price is available for this printing, so the ${PRICE_KEY_LABELS[estimatedFromKey]} price is shown instead.`}
            >
              Estimated from {PRICE_KEY_LABELS[estimatedFromKey]} price
            </span>
          )}
          {!isCalculating && card.languageMultiplierApplied && hasRealPrice(card.pointValue) && card.pointValue >= BULK_TIER_POINTS_THRESHOLD && (
            <span className="staged-card-price-annotation" title="Non-English card values are estimated using market data and may not reflect your card's exact market price.">
              {getLanguageDisplayName(card.language)} pricing
            </span>
          )}
        </div>
      </div>

      <button
        className="staged-card-remove"
        onClick={() => onRemove(card.id)}
        aria-label="Remove card"
      >
        <img
          src={AssetUrls.icons.dropdowns.close}
          alt="Remove"
          className="staged-card-remove-icon"
        />
      </button>
    </div>
  );
};

export default StagedCardItem;
